import ImporteField, { interpretarImporte } from "./ImporteField";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDataContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getPaymentInformation, paymentPf } from "../../services/api";
import Swal from "sweetalert2";
import dashboardStyle from "../../styles/Dashboard.module.css";
import pagoFacilImage from "../../images/comprobantes ui-pagofacil.png";
import PaymentReportForm, {
  PaymentField,
  PaymentModalLoader,
} from "./PaymentReportForm";

export default function PagoFacil({ toggleModal3, onFormularioEnviado }) {
  const navigate = useNavigate();
  const { contextData } = useDataContext();
  const customerId = contextData?.customer?.id;
  const orderId = contextData?.customer?.order_id;

  const [selectedDate, setSelectedDate] = useState(null);
  const [transaccion, setTransaccion] = useState("");
  const [terminal, setTerminal] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const singlePayment = 0;
  const paymentStatus = 0;
  const [amountError, setAmountError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pfFile, setPfFile] = useState(null);

  useEffect(() => {
    if (!customerId || !orderId) {
      setLoading(false);
      return;
    }

    let ignore = false;
    const fetchPaymentInformation = async () => {
      try {
        const data = await getPaymentInformation(customerId, orderId);
        if (!ignore) {
          setPaymentMethod(data.payment_method_number);
          setPaymentType(data.payment_type);
          setPfFile(data.pago_facil_file || null);
          setLoading(false);
        }
      } catch (error) {
        console.log(`Error en la solicitud de información de pago: ${error.message}`);
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchPaymentInformation();

    return () => {
      ignore = true;
    };
  }, [customerId, orderId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const monto = interpretarImporte(amount) ?? 0;
      if (monto <= 100) {
        setAmountError("El monto debe ser mayor que 100");
        return;
      }
      setAmountError("");

      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("selectedDate", selectedDate);
      formData.append("amount", monto);
      formData.append("transaccion", transaccion);
      formData.append("terminal", terminal);
      formData.append("paymentMethod", paymentMethod);
      formData.append("paymentType", paymentType);
      formData.append("singlePayment", singlePayment);
      formData.append("paymentStatus", paymentStatus);
      formData.append("archivoAdjunto", file);
      formData.append("customer", customerId);
      formData.append("order_id", orderId);

      const response = await paymentPf(formData, customerId);

      if (response.ok) {
        await response.json();
        onFormularioEnviado();
        navigate("/correcto");
      } else {
        await response.json();
        Swal.fire({
          icon: "error",
          iconColor: "#d54ab7",
          confirmButtonColor: "#0071ce",
          title: "Error al crear el pago",
          text: "Por favor, inténtelo de nuevo más tarde.",
        });
      }
    } catch (error) {
      console.error("Error al crear el pago:", error);
      Swal.fire({
        icon: "error",
        iconColor: "#d54ab7",
        confirmButtonColor: "#0071ce",
        title: "Error al crear el pago",
        text: "Por favor, inténtelo de nuevo más tarde.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileInputChange = (e) => {
    e.preventDefault();
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
  };

  if (loading) {
    return <PaymentModalLoader />;
  }

  const transaccionError =
    transaccion && !/^\d+$/.test(transaccion) ? "Ingresa solo números" : "";
  const terminalError =
    terminal && !/^\d+$/.test(terminal) ? "Ingresa solo números" : "";
  const amountInlineError = amountError;

  return (
    <PaymentReportForm
      methodName="PagoFácil"
      methodDescription="Usá los datos del ticket para informar el pago y dejarnos el comprobante listo para revisión."
      illustrationSrc={pagoFacilImage}
      illustrationAlt="Referencia visual del comprobante para PagoFácil"
      file={file}
      fileInputRef={fileInputRef}
      onFileChange={handleFileInputChange}
      onClose={toggleModal3}
      onSubmit={handleSubmit}
      submitDisabled={!file || isSubmitting}
      submitLabel={isSubmitting ? "Enviando..." : "Enviar informe"}
      uploadHint="Intentá que se vea bien el número de transacción y el número de terminal."
      uploadRequiredText="Subí el comprobante para poder enviar el informe."
    >
      {pfFile && (
        <a
          href={pfFile}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "block",
            marginBottom: 12,
            padding: "10px 14px",
            borderRadius: 10,
            background: "#eaf3ff",
            border: "1px solid #b9d7f8",
            color: "#0b5cab",
            fontWeight: 600,
            textAlign: "center",
            textDecoration: "none",
          }}
        >
          📎 Descargá acá el comprobante para pagar en Pago Fácil
        </a>
      )}
      <PaymentField label="Fecha de pago">
        <DatePicker
          placeholderText="Fecha del comprobante"
          className={dashboardStyle.paymentInput}
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="dd/MM/yyyy"
          maxDate={new Date()}
          required
        />
      </PaymentField>

      <PaymentField label="Número de transacción" error={transaccionError}>
        <input
          className={dashboardStyle.paymentInput}
          type="text"
          value={transaccion}
          onChange={(e) => setTransaccion(e.target.value)}
          pattern="[0-9]*"
          required
        />
      </PaymentField>

      <PaymentField label="Número de terminal" error={terminalError}>
        <input
          className={dashboardStyle.paymentInput}
          type="text"
          value={terminal}
          onChange={(e) => setTerminal(e.target.value)}
          pattern="[0-9]*"
          required
        />
      </PaymentField>

      <PaymentField label="Importe" error={amountInlineError}>
        <ImporteField value={amount} onChange={setAmount} className={dashboardStyle.paymentInput} />
      </PaymentField>
    </PaymentReportForm>
  );
}
